import { z } from "zod";
import type { ServiceProviderProps } from "../SRP";
import {
	CurrencySchema,
	type FundExplorerAPIResponse,
	type MetaField,
	type RawValue,
	type TypedAPIObj,
} from "./api.types";

/**
 * Enum schema defining valid fund explorer entity types
 */
export const FundExplorerEntitySchema = z.enum([
	"fund",
	"shareclass",
	"listing",
]);
export type FundExplorerEntity = z.infer<typeof FundExplorerEntitySchema>;

/**
 * Schema for entity keys that combines an entity type with a string identifier
 * @example "fund.name", "shareclass.isin", "listing.exchange"
 */
export const EntityKeySchema = z.custom<`${FundExplorerEntity}.${string}`>(
	(val) => {
		const isString = typeof val === "string";
		if (!isString) return false;
		const entities = ["fund", "shareclass", "listing", "profile"];
		const [entity, key] = val.split(".");
		if (!entities.includes(entity)) return false;
		return key.length > 0;
	},
	"value must have the following format (fund|shareclass|listing).<string-key>",
);
export type EntityKey = z.infer<typeof EntityKeySchema>;

/**
 * Configuration schema for search functionality
 */
export const SearchConfigSchema = z.object({
	placeholder: z.string(),
	searchOn: z.array(EntityKeySchema),
});
export type SearchConfig = z.infer<typeof SearchConfigSchema>;

/**
 * Schema defining valid filter comparison operators
 */
export const FilterOperandSchema = z
	.enum(["=", "<=", ">=", "<", ">", "!="])
	.default("=");
export type FilterOperand = z.infer<typeof FilterOperandSchema>;

/**
 * Schema for valid filter option values
 */
export const FilterOptionValueSchema = z
	.string()
	.or(z.number())
	.or(z.null())
	.or(z.boolean());
export type FilterOptionValue = z.infer<typeof FilterOptionValueSchema>;

/**
 * Configuration schema for individual filter options
 */
export const FilterOptionConfigSchema = z.object({
	key: z.string(),
	value: FilterOptionValueSchema.or(z.array(FilterOptionValueSchema)),
	label: z.string(),
	entityKey: EntityKeySchema.optional(),
	operand: FilterOperandSchema.optional(),
});
export type FilterOptionConfig = z.infer<typeof FilterOptionConfigSchema>;

/**
 * Configuration schema for individual filter groups
 */
export const FilterConfigSchema = z.object({
	columns: z.number().optional(),
	doNotPrune: z.boolean().optional(),
	entityKey: EntityKeySchema,
	hideWhenDisabled: z.boolean().optional(),
	key: z.string(),
	label: z.string(),
	layout: z.enum(["horizontal", "vertical"]).optional(),
	open: z.boolean().optional(),
	operand: FilterOperandSchema.optional(),
	options: z.array(FilterOptionConfigSchema),
	type: z.literal("single"),
	multiSelect: z.boolean().default(true),
});
export type FilterConfig = z.infer<typeof FilterConfigSchema>;

/**
 * Configuration schema for filter groups
 */
export const FilterGroupConfigSchema = z.object({
	title: z.string().optional(),
	key: z.string(),
	filters: z.array(FilterConfigSchema),
	type: z.literal("group"),
});
export type FilterGroupConfig = z.infer<typeof FilterGroupConfigSchema>;

/**
 * Configuration schema for filters or filter groups
 */
export const FilterOrFilterGroupConfigSchema = z.discriminatedUnion("type", [
	FilterConfigSchema,
	FilterGroupConfigSchema,
]);
export type FilterOrFilterGroupConfig = z.infer<
	typeof FilterOrFilterGroupConfigSchema
>;

/**
 * Configuration schema for all filters
 */
export const FiltersConfigSchema = z.object({
	title: z.string().optional(),
	filters: FilterOrFilterGroupConfigSchema.array(),
});
export type FiltersConfig = z.infer<typeof FiltersConfigSchema>;

/**
 * Base configuration schema for the Fund Explorer
 */
export const BaseFundExplorerConfigSchema = z.object({
	audience: z.string(),
	language: z.string(),
	search: SearchConfigSchema,
	filter: FiltersConfigSchema,
});
export type BaseFundExplorerConfig = z.infer<
	typeof BaseFundExplorerConfigSchema
>;

/**
 * Schema for active filters state
 */
export const ActiveFiltersSchema = z.record(z.string().array());
export type ActiveFilters = z.infer<typeof ActiveFiltersSchema>;

/**
 * Schema for scalar entity values
 */
export const ScalarValueSchema = z
	.string()
	.or(z.number())
	.or(z.boolean())
	.or(z.date())
	.nullable();
export type ScalarValue = z.infer<typeof ScalarValueSchema>;

/**
 * Schema for dictionary entity values
 */
export const DatumDictValueSchema = z
	.record(ScalarValueSchema)
	.or(z.object({}));
export type DatumDictValue = z.infer<typeof DatumDictValueSchema>;

/**
 * Schema for entity values, including scalar and dictionary values
 */
export const DatumValueSchema = ScalarValueSchema.or(DatumDictValueSchema);
export type DatumValue = z.infer<typeof DatumValueSchema>;

export const LocalIDSchema = z.string();
export type LocalID = z.infer<typeof LocalIDSchema>;

/**
 * Schema for generic data objects
 */
export const DatumSchema = z.record(DatumValueSchema).and(
	z.object({
		localId: LocalIDSchema,
	}),
);
export type Datum = z.infer<typeof DatumSchema>;

export const ListingSchema = DatumSchema.and(
	z.object({
		shareClassId: LocalIDSchema,
		fundId: LocalIDSchema,
	}),
);
export type Listing = z.infer<typeof ListingSchema>;

export const ShareClassSchema = DatumSchema.and(
	z.object({
		fundId: LocalIDSchema,
		listings: ListingSchema.array().optional(),
	}),
);
export type ShareClass = z.infer<typeof ShareClassSchema>;

export const ProfileSchema = DatumSchema.and(
	z.object({
		funds: LocalIDSchema.array().optional(),
	}),
);
export type Profile = z.infer<typeof ProfileSchema>;

/**
 * Schema for fund data including shareclasses and listings
 */
export const FundSchema = DatumSchema.and(
	z.object({
		shareclasses: ShareClassSchema.array(),
		profiles: LocalIDSchema.array().optional(),
		listings: ListingSchema.array().optional(),
		fund_badge_info: z.number().nullable(),
	}),
);
export type Fund = z.infer<typeof FundSchema>;

export type Entity = Fund | ShareClass | Profile;

export const IndexedFundSchema = z.object({
	type: z.literal("fund"),
	data: FundSchema,
});
export type IndexedFund = z.infer<typeof IndexedFundSchema>;

export const IndexedShareclassSchema = z.object({
	type: z.literal("shareclass"),
	data: ShareClassSchema,
});
export type IndexedShareclass = z.infer<typeof IndexedShareclassSchema>;

export const IndexedListingSchema = z.object({
	type: z.literal("listing"),
	data: ListingSchema,
});
export type IndexedListing = z.infer<typeof IndexedListingSchema>;

export const IndexedProfileSchema = z.object({
	type: z.literal("profile"),
	data: ProfileSchema,
});
export type IndexedProfile = z.infer<typeof IndexedProfileSchema>;

export const IndexedDatumSchema = z.discriminatedUnion("type", [
	IndexedFundSchema,
	IndexedShareclassSchema,
	IndexedListingSchema,
	IndexedProfileSchema,
]);
export type IndexedDatum = z.infer<typeof IndexedDatumSchema>;

export const IndexedDataSchema = z.record(LocalIDSchema, IndexedDatumSchema);
export type IndexedData = z.infer<typeof IndexedDataSchema>;

/**
 * Type defining the store state
 */
export type StoreState = {
	locale: string;
	config?: BaseFundExplorerConfig;
	activeSearch: string;
	activeFilters: ActiveFilters;
	appliedFilters: ActiveFilters;
	funds: Fund[];
	profiles: Profile[];
	filteredFunds: Fund[];
	filteredEntities: string[];
	isLoading: boolean;
	indexedData: IndexedData;
	loadingStatus: string | null;
	asAtDate: Date | null;
	apiResponse: FundExplorerAPIResponse | null;
	srpProps: ServiceProviderProps | null;
};

/**
 * Type defining available store actions
 */
export type StoreActions = {
	doFilter: (
		filterKey: FilterConfig["key"],
		optionKeys: FilterOptionConfig["key"][],
	) => void;
	doFilterReset: (args?: { exclude?: string[]; include?: string[] }) => void;
	doSearch: (term: string) => void;
	doSearchReset: () => void;
	init: (config: BaseFundExplorerConfig, data: FundExplorerAPIResponse) => void;
	refine: (applyURLParams?: boolean) => void;
	doSetLoadingStatus: (status: string | null) => void;
	doGetFilterOptionResultsCount: (
		filterKey: FilterConfig["key"],
		optionKey: FilterOptionConfig["key"],
	) => number;
	__doUpdateFilteredFunds: (funds: Fund[]) => void;
	__setState: (state: Partial<StoreState>) => void;
};

/**
 * Props interface for the display component
 */
export type DisplayComponentProps = {
	funds?: Fund[];
	config?: BaseFundExplorerConfig;
	filteredFunds?: Fund[];
	isLoading?: boolean;
	filteredEntities?: string[];
};

/**
 * Props interface for the search component
 */
export type SearchComponentProps = {
	onSearch: (term: string) => void;
	onReset: () => void;
	activeSearch?: string;
};

/**
 * Props interface for the filter component
 */
export type FilterComponentProps = {
	onFilter: (
		filterKey: FilterConfig["key"],
		optionKeys: FilterOptionConfig["key"][],
	) => void;
	onReset: StoreActions["doFilterReset"];
	filterOptionCountFn: (
		filterKey: FilterConfig["key"],
		optionKey: FilterOptionConfig["key"],
	) => number;
	activeFilters: ActiveFilters;
	filtersConfig?: FiltersConfig;
	activeSearch?: string;
};

/**
 * Type definitions for core components
 */
export type SearchComponent = (
	props: SearchComponentProps,
) => React.ReactElement;
export type DisplayComponent = (
	props: DisplayComponentProps,
) => React.ReactElement;
export type FilterComponent = (
	props: FilterComponentProps,
) => React.ReactElement;
export type GenericComponent = (
	props: Record<string, unknown>,
) => React.ReactElement;

/**
 * Type definitions for typed components
 */
export type SearchComponentTyped = {
	type: "search";
	node: SearchComponent;
};
export type DisplayComponentTyped = {
	type: "display";
	node: DisplayComponent;
};
export type FilterComponentTyped = {
	type: "filter";
	node: FilterComponent;
};
export interface GenericComponentTyped {
	type: "generic";
	node: GenericComponent;
	props?: Record<string, unknown>;
}

/**
 * Union type for all possible component types
 */
export type Component =
	| GenericComponentTyped
	| SearchComponentTyped
	| DisplayComponentTyped
	| FilterComponentTyped;

/**
 * Interface for module configuration
 */
export interface Module {
	slot: string;
	component: Component;
	rank?: number;
}

/**
 * Props interface for the layout component
 */
export type LayoutComponentProps = {
	renderSlot: (slot: string) => React.ReactNode;
};

/**
 * Type definition for the layout component
 */
export type LayoutComponent = (
	props: LayoutComponentProps,
) => React.ReactElement;

/**
 * Props interface for the fund explorer kernel component
 */
export type KernelProps = {
	layout: LayoutComponent;
	modules: Module[];
	initialState: Partial<StoreState>;
	url: string;
	configId: number;
	apiId: number;
	audienceId: number;
	languageId?: number;
	version?: "live" | "preview" | "draft";
	action?: string;
	tenant?: string;
};

/**
 * Props interface for the wrap component
 */
export type WrapProps<T> = {
	component: T;
};

export type Formatter<
	R = string | null | Record<string, string> | Record<string, string>[],
> = (meta: MetaField, obj: TypedAPIObj, value: RawValue) => R;

export const CurrencyFormatSchema = z.record(CurrencySchema, z.string());
export type CurrencyFormat = z.infer<typeof CurrencyFormatSchema>;

export type InitialStateArgs = {
	filters?: string[];
	search?: string;
};
